/**
 * /venues/:venueSlug/standings — the season table, public.
 *
 * Renders inside AppShell. Two calls in sequence, the same shape as
 * VenueLanding: by-slug turns the URL's slug into the numeric id, then
 * public-standings reads the active season off it.
 *
 * WHICH COLUMNS APPEAR is the server's decision, not this component's. The
 * endpoint applies the venue's standings_show_* toggles and returns a `show`
 * descriptor plus rows that simply omit the disabled fields — so the header
 * row and the body are driven from the same object and cannot disagree.
 *
 * finishIn9 is the one toggle deliberately ignored. A first-9 average is a
 * per-tournament figure; there is no season-level column for it, and the
 * schema flag predates that decision.
 */

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiGet } from '../lib/api.js';
import { useAuth, useVenue } from '../context/AuthContext.jsx';
import { formatDateShort, formatAvg } from '../lib/format.js';

/** Season line: 'Season 22 — 1 Sep 2026 to 31 Aug 2027', either half optional. */
function seasonDates(season) {
  const from = formatDateShort(season.startDate);
  const to   = formatDateShort(season.endDate);

  if (from && to) return `${from} to ${to}`;
  if (from)       return `started ${from}`;
  return null;
}

function LegDiff({ value }) {
  const cls = value > 0 ? 'hd-diff-up' : value < 0 ? 'hd-diff-down' : 'hd-diff-zero';
  return <span className={cls}>{value > 0 ? `+${value}` : value}</span>;
}

export default function PublicStandings() {
  const { venueSlug } = useParams();
  const { user } = useAuth();
  const membership = useVenue(venueSlug);

  const [state, setState] = useState({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });

    apiGet(`/api/venues/by-slug/${venueSlug}`).then(async ({ data, error }) => {
      if (cancelled) return;

      if (error || !data?.venue) {
        setState({ status: 'error', error: error ?? 'Venue not found' });
        return;
      }

      const table = await apiGet(
        `/api/venues/${data.venue.id}/public-standings?limit=100`
      );

      if (cancelled) return;

      if (table.error) {
        setState({ status: 'error', error: table.error, venue: data.venue });
        return;
      }

      setState({ status: 'ready', venue: data.venue, table: table.data });
    });

    return () => { cancelled = true; };
  }, [venueSlug]);

  if (state.status === 'loading') {
    return <div className="hd-empty">Loading…</div>;
  }

  if (state.status === 'error') {
    return (
      <div className="hd-stack">
        <h1 className="hd-page-title">
          {state.venue ? 'Standings unavailable' : 'Venue not found'}
        </h1>
        <p className="hd-muted">
          {state.venue
            ? state.error
            : <>No venue at <code>/{venueSlug}</code>.</>}
          {' '}
          <Link to={state.venue ? `/venues/${venueSlug}` : '/'}>
            {state.venue ? 'Back to the venue' : 'Back to the home page'}
          </Link>
        </p>
      </div>
    );
  }

  const { venue, table } = state;
  const { season, show, standings } = table;

  // The CSV route takes venue_admin, so the button only appears for somebody it
  // will actually answer. Rendering it for everyone would hand a spectator a
  // link to a 403.
  const canExport = Boolean(
    season && (
      ['platform_owner', 'super_admin'].includes(user?.platformRole) ||
      ['venue_admin', 'venue_owner'].includes(membership?.role)
    )
  );

  return (
    <div className="hd-stack">
      <div className="hd-row-between">
        <div>
          <h1 className="hd-page-title">Season standings</h1>
          <p className="hd-meta">
            <Link to={`/venues/${venue.slug}`}>{venue.name}</Link>
            {season && <> · {season.name}</>}
            {season && seasonDates(season) && <> — {seasonDates(season)}</>}
          </p>
        </div>

        {canExport && (
          <a
            className="hd-btn hd-btn-quiet hd-btn-sm"
            href={`/api/venues/${venue.id}/seasons/${season.id}/export-standings`}
          >
            Export CSV
          </a>
        )}
      </div>

      {!season && (
        <div className="hd-empty">No active season at this venue.</div>
      )}

      {season && standings.length === 0 && (
        <div className="hd-empty">
          No results yet — standings appear after the first tournament completes.
        </div>
      )}

      {season && standings.length > 0 && (
        <>
          <div className="hd-table-wrap">
            <table className="hd-table hd-standings-table">
              <thead>
                <tr>
                  <th style={{ width: 36 }}>#</th>
                  <th>Player</th>
                  <th className="hd-table-num" title="Tournaments played">P</th>
                  <th className="hd-table-num" title="Points">Pts</th>
                  <th className="hd-table-num" title="Matches won">MW</th>
                  <th className="hd-table-num" title="Matches lost">ML</th>
                  <th className="hd-table-num" title="Legs won">LW</th>
                  <th className="hd-table-num" title="Legs lost">LL</th>
                  <th className="hd-table-num" title="Leg difference">+/−</th>
                  <th className="hd-table-num" title="Season average">Avg</th>
                  <th className="hd-table-num">180s</th>
                  {show.oneForties && (
                    <th className="hd-table-num" title="140+ scores">140+</th>
                  )}
                  {show.hundreds && (
                    <th className="hd-table-num" title="100+ scores">100+</th>
                  )}
                  {show.breaks && (
                    <th className="hd-table-num" title="Breaks of throw">Breaks</th>
                  )}
                  <th className="hd-table-num" title="Highest checkout">HC</th>
                </tr>
              </thead>

              <tbody>
                {standings.map((row) => (
                  <tr key={`${row.position}-${row.userId ?? row.displayName}`}>
                    <td className="hd-muted">{row.position}</td>
                    <td>
                      {row.displayName ?? 'Unknown player'}
                      {row.isGuest && <span className="hd-guest-tag">(guest)</span>}
                    </td>
                    <td className="hd-table-num">{row.tournamentsPlayed}</td>
                    <td className="hd-table-num hd-standings-pts">{row.totalPoints}</td>
                    <td className="hd-table-num">{row.matchesWon}</td>
                    <td className="hd-table-num">{row.matchesLost}</td>
                    <td className="hd-table-num">{row.legsWon}</td>
                    <td className="hd-table-num">{row.legsLost}</td>
                    <td className="hd-table-num">
                      <LegDiff value={row.legDifference} />
                    </td>
                    <td className="hd-table-num">{formatAvg(row.seasonAvg)}</td>
                    <td className="hd-table-num">{row.total180s}</td>
                    {show.oneForties && (
                      <td className="hd-table-num">{row.total140Plus}</td>
                    )}
                    {show.hundreds && (
                      <td className="hd-table-num">{row.total100Plus}</td>
                    )}
                    {show.breaks && (
                      <td className="hd-table-num">{row.totalBreaks}</td>
                    )}
                    <td className="hd-table-num">{row.highestCheckout}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="hd-hint">
            {table.playerCount} player{table.playerCount === 1 ? '' : 's'} this season.
            Ranked on points, then leg difference, then legs won, then average.
          </p>
        </>
      )}
    </div>
  );
}
